import React, { Component } from 'react';
import Tagify from '@yaireo/tagify/dist/tagify';
import { MODAL_SAVE_LAYERS } from '@/util/enums/EnumModals';
import flex from '@/css/module/flex.css';
import CategoryRef from '@/util/CategoryRef';
import { QUERY_NAME_INPUT } from '@/constants/test-ids';
import { SavedQueriesWidget } from '@/features/savedQueriesWidget/SavedQueriesWidget';
import ViewRef from '@/util/ViewRef';
import { GeneralStylesWidgetContainer } from '@/features/savedQueriesWidget/GeneralStylesWidgetContainer';
import { SAVE_MODAL } from '@/features/savedQueriesWidget/widgetRenderContext.constants';
import { Button } from '@trevor/ui-components';
import { get } from '@/util/helpers/ajax-helpers';
import AjaxSpinner from '../../AjaxSpinner';
import { SaveTypeSelect } from './subcomponents/SaveTypeSelect';

class ModalSaveLayers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      existingTags: [],
      categoriesOpen: false,
      selectedQueryToReplace: null,
    };
  }

  static modalType() {
    return MODAL_SAVE_LAYERS;
  }

  static reducer(modals) {
    return modals.saveLayersModal;
  }

  static title() {
    return <span>Save query as</span>;
  }

  componentDidMount() {
    this.takeFocus();
    this.configureAvailableTags();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.categoriesOpen !== this.state.categoriesOpen) {
      this.configureAvailableTags();
    }
  }

  takeFocus = () =>
    setTimeout(() => {
      if (this._input) {
        this._input.select();
      }
    }, 500);

  onTextFieldChange(userInteraction) {
    return (section) => (e) => {
      const props = { section, newValue: e.target.value };
      userInteraction(props)();
    };
  }

  onRadioButtonChange = (userInteraction) => (section) => (e) => {
    const props = { section, newValue: e.currentTarget.value };
    this.setState({ selectedQueryToReplace: null });
    userInteraction(props)();
  };

  viewChooser(state, userInteraction) {
    if (!state.overwriteExisting) {
      const textFieldChange = this.onTextFieldChange(userInteraction);
      const chosen = (
        <input
          id={QUERY_NAME_INPUT}
          key="insight-name"
          placeholder="Give your query a name"
          className="form-control set-input-height"
          onChange={textFieldChange('nameChanged')}
          value={state.name ?? ''}
          style={{ height: '45px' }}
          ref={(input) => (this._input = input)}
        />
      );
      return { chosen };
    }

    const selectQuery = (savedQuery) => {
      // TODO: Remove this `mapping` once we get rid of ViewRef!!!
      this.setState({ selectedQueryToReplace: savedQuery });
      const viewRef = ViewRef.fromSavedQuery(savedQuery);
      userInteraction({ section: 'existingChosen', view: viewRef })(undefined);
    };

    const deselectQuery = (e) => {
      e.preventDefault();
      userInteraction({ section: 'existingChosen', view: null })(e);
    };

    if (!state.existing) {
      const chosen = (
        <input
          key="disabled-input"
          type="text"
          className="form-control set-input-height"
          disabled
        />
      );
      const options = (
        <GeneralStylesWidgetContainer>
          <SavedQueriesWidget onSelectQuery={selectQuery} mode={SAVE_MODAL} />
        </GeneralStylesWidgetContainer>
      );
      return { chosen, options };
    }
    const chosen = (
      <button
        type="button"
        className="btn btn-outline-dark full-width set-input-height"
        onClick={deselectQuery}
      >
        {state.existing.name()}
      </button>
    );
    return { chosen };
  }

  overwriteExisting(state, userInteraction) {
    const newExistingChange =
      this.onRadioButtonChange(userInteraction)('newExistingChanged');
    return (
      <td colSpan="4" className={`${flex.alignItemsCenter} padding-bottom-15`}>
        <label className={`${flex.alignItemsCenter} radio-inline`}>
          <input
            type="radio"
            name="overwrite_existing"
            value="new"
            checked={!state.overwriteExisting}
            onChange={newExistingChange}
            style={{ marginTop: '0px' }}
            onClick={this.takeFocus}
          />
          <b>Save new</b>
        </label>
        <label className={`${flex.alignItemsCenter} radio-inline`}>
          <input
            type="radio"
            name="overwrite_existing"
            value="existing"
            checked={state.overwriteExisting}
            style={{ marginTop: '0px' }}
            onChange={newExistingChange}
          />
          <b>Replace existing query</b>
        </label>
      </td>
    );
  }

  prepareTagsField() {
    const inputTags = document.getElementById('queryTagsTxt');
    if (inputTags) {
      const tagify = new Tagify(inputTags, this.tagsFieldSettings());
      tagify.on('add', (e) => {
        const props = {
          section: 'view-tag-added',
          tag: CategoryRef.buildFromTagify(e.detail.data),
        };
        this.props.userInteraction(props)();
      });
      tagify.on('remove', (e) => {
        const props = {
          section: 'view-tag-removed',
          tag: CategoryRef.buildFromTagify(e.detail.data),
        };
        this.props.userInteraction(props)();
      });
      tagify.limitMaxLength = (() => {
        const allowedKeys = [
          'Backspace',
          'Delete',
          'ArrowUp',
          'ArrowDown',
          'ArrowLeft',
          'ArrowRight',
        ];
        // search for the input inside tagify
        const input = document.getElementsByClassName('tagify__input')[0];
        // onkeydown, verify if its textContent length exceeds maximum allowed
        input.onkeydown = (event) => {
          if (
            !allowedKeys.includes(event.key) &&
            input.textContent.length >= 40
          ) {
            event.preventDefault();
          }
        };
      })();
    }
  }

  configureAvailableTags() {
    // Retrieve available tags to be used in the modal
    get('loading categories', '/views/tags').then((response) => {
      if (response && response.length > 0) {
        this.setState({
          existingTags: response
            .map((tag) => CategoryRef.build(tag))
            .sort((t1, t2) => t1.name().localeCompare(t2.name())),
        });
      }
      this.prepareTagsField();
    });
  }

  tagsFieldSettings() {
    const whitelist = this.state.existingTags.map((category) => ({
      id: category.id(),
      value: category.name(),
      count: category.count(),
    }));
    return {
      pattern: /^.{0,40}$/, // Validate typed tag(s) by Regex. Here maximum chars length is defined as "40"
      delimiters: ',',
      templates: {
        tag(tagData) {
          return `<tag title="${
            tagData.value
          }" contenteditable="false" spellcheck="false" class="tagify__tag ${
            tagData.class ? tagData.class : ''
          }" ${this.getAttributes(tagData)}>
					<x title="remove tag" class="tagify__tag__removeBtn" style="height: 20px"></x>
					<div>
						<span class="tagify__tag-text" style="height: 20px">${tagData}</span>
					</div>
				</tag>`;
        },
        dropdownItem(tagData) {
          return `<div class="tagify__dropdown__item ${
            tagData.class ? tagData.class : ''
          }" tagifySuggestionIdx="${tagData.tagifySuggestionIdx}">
						<span>${tagData.value} (${tagData.count})</span>
					</div>`;
        },
      },
      dropdown: {
        enabled: 0,
        maxItems: Infinity,
        highlightFirst: false,
        fuzzySearch: true,
        mapValueTo: '_name',
      },
      editTags: 0,
      autocomplete: true,
      whitelist,
      placeholder: 'Add new or existing categories',
      duplicates: false, // allow adding duplicate tags
      enforceWhitelist: false, // only allow tags that come from whitelist,
    };
  }

  render() {
    const { userInteraction, state } = this.props;
    if (state.saving) {
      return (
        <span>
          <AjaxSpinner small center />
        </span>
      );
    }
    const existingTags =
      state.existing &&
      state.existing.categories() &&
      state.existing
        .categories()
        .map((category) => ({ id: category.id(), value: category.name() }))
        .sort((t1, t2) => t1.value.localeCompare(t2.value));

    const viewChooser = this.viewChooser(state, userInteraction);
    return (
      <table className="full-width" style={{ fontSize: '13px' }}>
        <tbody>
          {!(state.existing && !state.overwriteExisting) && (
            <tr>
              <SaveTypeSelect
                selectedQueryToReplace={this.state.selectedQueryToReplace}
                takeFocus={this.takeFocus}
                onChange={this.onRadioButtonChange(userInteraction)(
                  'newExistingChanged',
                )}
                overwriteExisting={state.overwriteExisting}
              />
            </tr>
          )}
          <tr>
            <td colSpan="4" className="vertical-align-center">
              <label id="titleLabel">
                {state.overwriteExisting ? 'Replace query' : 'Name your query'}
              </label>
            </td>
          </tr>
          <tr>
            <td colSpan="4" className="vertical-align-center">
              {viewChooser.chosen}
            </td>
          </tr>

          <tr>
            <td colSpan="4" className="vertical-align-center">
              <label id="titleTagsLabel" className="margin-top-15">
                Categorize this query
              </label>
            </td>
          </tr>

          <tr>
            <td colSpan="4" className="vertical-align-center">
              {this.state.categoriesOpen ||
              (existingTags && existingTags.length > 0) ? (
                <input
                  type="text"
                  name="queryTagsTxt"
                  id="queryTagsTxt"
                  className="form-control tagInputFormControl"
                  style={{ height: '45px' }}
                  defaultValue={
                    existingTags && existingTags.length > 0
                      ? JSON.stringify(existingTags)
                      : null
                  }
                  autoComplete="off"
                />
              ) : (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => this.setState({ categoriesOpen: true })}
                >
                  Add category
                </Button>
              )}
            </td>
          </tr>
          <tr>
            <td colSpan="4">{viewChooser.options}</td>
          </tr>
        </tbody>
      </table>
    );
  }
}

export default ModalSaveLayers;
